import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Chip, LinearProgress,
  Alert, Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  EmojiEvents as TrophyIcon,
  MenuBook as BookIcon,
  Lock as LockIcon,
  PlayCircle as InProgressIcon,
  Timeline as TimelineIcon,
  Grade as GradeIcon,
  HourglassEmpty as UpcomingIcon,
  AccountTree as FilierIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import Loading from '../common/Loading';

const SESSION_TYPE_KEY = (semNum) => semNum % 2 === 1 ? 'dp_semAutumn' : 'dp_semSpring';

const YEAR_LABEL_KEY = (yr) => {
  const keys = ['', 'dp_year1', 'dp_year2', 'dp_year3', 'dp_year4', 'dp_year5'];
  return keys[yr] || null;
};

const getDiplomaYears = (lib_etp = '', cod_etp = '', maxSem = 0) => {
  const name = (lib_etp + ' ' + cod_etp).toLowerCase();
  if (name.includes('licence') || name.includes('ليسانس') || name.includes('bac+3')) return 3;
  if (name.includes('master') || name.includes('ماستر') || name.includes('bac+5')) return 2;
  if (name.includes('doctorat') || name.includes('دكتوراه') || name.includes('phd')) return 3;
  if (name.includes('dut') || name.includes('bts') || name.includes('deug')) return 2;
  if (name.includes('ingénieur') || name.includes('مهندس')) return 5;
  return Math.max(Math.ceil(maxSem / 2), 1);
};

// ── Semester badge used in roadmap ───────────────────────────────────────────
const SemBadge = ({ semNum, pct, hasData, t }) => {
  const label = `S${semNum}`;
  const bg = !hasData ? '#e0e0e0' : pct === 100 ? '#27ae60' : pct > 0 ? '#3498db' : '#e0e0e0';
  const tooltipText = hasData ? `${label}: ${pct}%` : `${label}: ${t('dp_notStarted')}`;
  return (
    <Tooltip title={tooltipText}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 52 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '50%',
          bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: hasData && pct === 100 ? '0 0 0 3px #27ae6040' : 'none'
        }}>
          {!hasData
            ? <LockIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
            : pct === 100
              ? <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
              : <Typography variant="caption" fontWeight={700} sx={{ color: 'white', fontSize: 10 }}>{pct}%</Typography>
          }
        </Box>
        <Typography variant="caption" fontWeight={600} sx={{ color: hasData ? 'text.primary' : 'text.disabled' }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Diploma Roadmap ───────────────────────────────────────────────────────────
const DiplomaRoadmap = ({ totalYears, byYear, semesterMap, t }) => {
  const years = Array.from({ length: totalYears }, (_, i) => i + 1);

  return (
    <Paper elevation={0} sx={{
      borderRadius: 3, mb: 3,
      border: '1.5px solid #e8ecef',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e8f4fd 100%)',
      overflow: 'hidden'
    }}>
      <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid #e8ecef', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimelineIcon sx={{ fontSize: 18, color: '#3498db' }} />
        <Typography variant="subtitle2" fontWeight={700} color="#3498db">
          {t('dp_roadmap')}
        </Typography>
        <Chip label={t('dp_years', { count: totalYears })} size="small" variant="outlined" color="primary" sx={{ height: 20, borderRadius: 1.5, fontSize: '0.72rem' }} />
      </Box>

      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 0, overflowX: 'auto' }}>
        {years.map((yr, idx) => {
          const yearSems = byYear[yr] || [];
          const yearValidated = yearSems.reduce((s, sem) => s + sem.validated_count, 0);
          const yearTotal = yearSems.reduce((s, sem) => s + sem.total_count, 0);
          const yearPct = yearTotal > 0 ? Math.round((yearValidated / yearTotal) * 100) : 0;
          // hasData = year exists in hierarchy (even if not started yet)
          const hasData = yearSems.length > 0 && yearSems.some(s => s.has_started !== undefined ? s.total_count > 0 : true);

          const completed = hasData && yearPct === 100;
          const inProgress = hasData && yearPct < 100;

          const border = completed ? '#27ae60' : inProgress ? '#3498db' : '#bdbdbd';
          const bg = completed ? '#e8f5e9' : inProgress ? '#e3f2fd' : '#f5f5f5';
          const labelColor = completed ? '#27ae60' : inProgress ? '#3498db' : '#9e9e9e';

          const s1num = yr * 2 - 1;
          const s2num = yr * 2;
          const sem1 = semesterMap[s1num];
          const sem2 = semesterMap[s2num];

          const yearLabelKey = YEAR_LABEL_KEY(yr);

          return (
            <React.Fragment key={yr}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 130, flex: 1 }}>
                <Box sx={{ width: '100%', borderRadius: 2, border: `2px solid ${border}`, bgcolor: bg, p: 1.5, textAlign: 'center', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    {completed
                      ? <CheckCircleIcon sx={{ color: '#27ae60', fontSize: 16 }} />
                      : inProgress
                        ? <InProgressIcon sx={{ color: '#3498db', fontSize: 16 }} />
                        : <LockIcon sx={{ color: '#9e9e9e', fontSize: 16 }} />
                    }
                    <Typography variant="caption" fontWeight={700} sx={{ color: labelColor }}>
                      {yearLabelKey ? t(yearLabelKey) : `${t('dp_year_generic')} ${yr}`}
                    </Typography>
                  </Box>
                  {hasData ? (
                    <>
                      <LinearProgress variant="determinate" value={yearPct} sx={{
                        height: 5, borderRadius: 3, bgcolor: '#ffffff80',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: completed ? '#27ae60' : '#3498db' }
                      }} />
                      <Typography variant="caption" sx={{ color: labelColor, fontWeight: 600 }}>{yearPct}%</Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.disabled">{t('dp_notStarted')}</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                  <SemBadge semNum={s1num} pct={sem1 ? Math.round((sem1.validated_count / sem1.total_count) * 100) : 0} hasData={!!(sem1 && sem1.has_started)} t={t} />
                  <SemBadge semNum={s2num} pct={sem2 ? Math.round((sem2.validated_count / sem2.total_count) * 100) : 0} hasData={!!(sem2 && sem2.has_started)} t={t} />
                </Box>
              </Box>
              {idx < years.length - 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, mt: 1, color: '#bdbdbd', fontSize: 22, fontWeight: 300 }}>›</Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

// ── Module row (same pattern as GradeTable) ───────────────────────────────────
const ModuleRow = ({ mod, accent }) => {
  const noteVal = mod.not_elp !== null && mod.not_elp !== undefined ? parseFloat(mod.not_elp) : null;
  const upcoming = !mod.has_grade;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 1, px: 2, py: 1.5,
      borderRadius: 2,
      bgcolor: upcoming ? '#fafafa' : 'white',
      border: `1px solid ${upcoming ? '#ebebeb' : '#f0f0f0'}`,
      opacity: upcoming ? 0.72 : 1,
      '&:hover': { bgcolor: upcoming ? '#f5f5f5' : '#fafafa', borderColor: accent + '55' },
      transition: 'all 0.15s',
    }}>
      {/* Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
          bgcolor: upcoming ? '#f0f0f0' : accent + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {upcoming
            ? <UpcomingIcon sx={{ fontSize: 17, color: '#bdbdbd' }} />
            : <GradeIcon sx={{ fontSize: 17, color: accent }} />
          }
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap color={upcoming ? 'text.secondary' : 'text.primary'}>
            {mod.lib_elp_arb || mod.lib_elp || '—'}
          </Typography>
          {mod.lib_elp_arb && mod.lib_elp && (
            <Typography variant="caption" color="text.disabled" noWrap display="block">{mod.lib_elp}</Typography>
          )}
          {!upcoming && mod.cod_anu && (
            <Typography variant="caption" sx={{ color: accent, opacity: 0.75, fontWeight: 600 }}>
              {mod.cod_anu}/{String(parseInt(mod.cod_anu) + 1).slice(-2)}
              {mod.cod_ses === '2' || mod.cod_ses === 2 ? ' · S2' : ''}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Code + note + status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{
          fontFamily: 'monospace', px: 1, py: 0.3, borderRadius: 1, fontWeight: 600,
          bgcolor: upcoming ? '#f5f5f5' : '#f1f5f9',
          color: upcoming ? '#9e9e9e' : '#475569',
        }}>
          {mod.grade_cod_elp || mod.cod_elp}
        </Typography>
        {upcoming ? (
          <Typography variant="caption" color="text.disabled" sx={{ width: 52, textAlign: 'center' }}>—</Typography>
        ) : noteVal !== null ? (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 26, borderRadius: 1.5, fontWeight: 700, fontSize: '0.82rem',
            bgcolor: mod.is_validated ? '#dcfce7' : '#fee2e2',
            color: mod.is_validated ? '#15803d' : '#dc2626',
          }}>
            {noteVal.toFixed(2)}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ width: 52, textAlign: 'center' }}>—</Typography>
        )}
        {upcoming ? (
          <Chip
            label="À venir"
            size="small"
            sx={{ height: 22, borderRadius: 1.5, fontWeight: 600, bgcolor: '#f5f5f5', color: '#9e9e9e', fontSize: '0.68rem' }}
          />
        ) : mod.is_validated ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            label={mod.cod_tre || 'V'}
            size="small"
            sx={{
              height: 22, borderRadius: 1.5, fontWeight: 700,
              bgcolor: ['ACR','VE'].includes(mod.cod_tre) ? '#dbeafe' : mod.cod_tre === 'VAR' ? '#fef3c7' : '#dcfce7',
              color:   ['ACR','VE'].includes(mod.cod_tre) ? '#2563eb' : mod.cod_tre === 'VAR' ? '#b45309' : '#16a34a',
            }}
          />
        ) : (
          <Chip
            icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
            label="NV"
            size="small"
            sx={{ height: 22, borderRadius: 1.5, fontWeight: 700, bgcolor: '#fee2e2', color: '#dc2626' }}
          />
        )}
      </Box>
    </Box>
  );
};

const SEM_COLORS = {
  1: '#3498db', 2: '#e67e22', 3: '#2ecc71',
  4: '#9b59b6', 5: '#e74c3c', 6: '#34495e'
};

// ── Main Component ────────────────────────────────────────────────────────────
const StudentDiplomaProgress = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    studentAPI.getDiplomaProgress()
      .then(res => setData(res))
      .catch(err => setError(err.response?.data?.error || t('dp_error')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading message={t('dp_loading')} />;
  if (error)   return <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>{error}</Alert>;

  const { diploma, filiere, summary, by_semester = [] } = data;

  // Group by year
  const byYear = {};
  by_semester.forEach(sem => {
    const yr = Math.ceil(sem.semester_number / 2);
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(sem);
  });

  const semesterMap = {};
  by_semester.forEach(sem => { semesterMap[sem.semester_number] = sem; });

  // totalYears: prefer hierarchy (always 6 sems = 3 years for licence), else heuristic
  const maxSem = by_semester.length > 0 ? Math.max(...by_semester.map(s => s.semester_number)) : 0;
  const totalYears = filiere ? Math.ceil(maxSem / 2) : getDiplomaYears(diploma?.lib_etp || '', diploma?.cod_etp || '', maxSem);
  const allYears = Array.from({ length: Math.max(totalYears, 1) }, (_, i) => i + 1);
  const fullByYear = {};
  allYears.forEach(yr => { fullByYear[yr] = byYear[yr] || []; });

  const progressPct = summary?.progress_pct || 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TrophyIcon sx={{ fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{t('dp_title')}</Typography>
          {filiere && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, opacity: 0.85 }}>
              <FilierIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" fontWeight={600} noWrap>
                {filiere.label_ar || filiere.label_fr || filiere.cod}
              </Typography>
            </Box>
          )}
          {!filiere && diploma && (
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }} noWrap>
              {diploma.lib_etp || diploma.cod_etp}
            </Typography>
          )}
        </Box>
        {summary && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
            <Chip label={`${summary.validated} / ${summary.total}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, height: 24 }} />
            <Chip
              label={`${progressPct}%`}
              size="small"
              sx={{ bgcolor: progressPct === 100 ? '#27ae60' : 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, height: 24 }}
            />
          </Box>
        )}
      </Box>

      {/* ── Roadmap ── */}
      <DiplomaRoadmap totalYears={totalYears} byYear={fullByYear} semesterMap={semesterMap} t={t} />

      {/* ── Overall Progress Bar ── */}
      {summary && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" fontWeight={700}>{t('dp_overallProgress')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dp_unitsAcquired', { validated: summary.validated, total: summary.total })}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressPct} sx={{
            height: 12, borderRadius: 6, bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: progressPct === 100 ? '#27ae60' : '#3498db' }
          }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            {progressPct === 100
              ? t('dp_congrats')
              : t('dp_remainingMsg', { count: summary.remaining })}
          </Typography>
        </Paper>
      )}

      {/* ── Empty state ── */}
      {by_semester.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <BookIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600}>{t('dp_noData')}</Typography>
          <Typography variant="body2" color="text.disabled">{t('dp_noDataDesc')}</Typography>
        </Paper>
      )}

      {/* ── Per-year sections ── */}
      {allYears.map(year => {
        const semesters = fullByYear[year];
        // Year not started = no semester in this year has any graded module
        const yearNotStarted = semesters.length === 0 || semesters.every(s => !s.has_started);
        const yearValidated = semesters.reduce((s, sem) => s + sem.validated_count, 0);
        const yearTotal    = semesters.reduce((s, sem) => s + sem.total_count, 0);
        const yearPct = yearTotal > 0 ? Math.round((yearValidated / yearTotal) * 100) : 0;
        const yearLabelKey = YEAR_LABEL_KEY(year);
        const yearLabel = yearLabelKey ? t(yearLabelKey) : `${t('dp_year_generic')} ${year}`;

        return (
          <Box key={year} sx={{ mb: 3 }}>

            {/* Year header */}
            <Box sx={{
              background: yearNotStarted
                ? 'linear-gradient(135deg, #37474f 0%, #546e7a 100%)'
                : 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 100%)',
              borderRadius: 3, px: 3, py: 2, mb: 2, color: 'white',
              display: 'flex', alignItems: 'center', gap: 1.5,
              opacity: yearNotStarted ? 0.65 : 1,
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {yearNotStarted
                  ? <LockIcon sx={{ fontSize: 16 }} />
                  : yearPct === 100
                    ? <CheckCircleIcon sx={{ fontSize: 18 }} />
                    : <InProgressIcon sx={{ fontSize: 18 }} />
                }
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>{yearLabel}</Typography>
              {yearNotStarted ? (
                <Chip label={t('dp_notStarted')} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', height: 22, borderRadius: 1.5 }} />
              ) : (
                <Chip
                  label={`${yearValidated}/${yearTotal} · ${yearPct}%`}
                  size="small"
                  sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, height: 22, borderRadius: 1.5 }}
                />
              )}
            </Box>

            {/* Semesters — always render (even for future years from hierarchy) */}
            {semesters.length === 0 ? (
              /* No hierarchy data for this year — fallback locked placeholder */
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[year * 2 - 1, year * 2].map(sn => (
                  <Paper key={sn} elevation={0} sx={{
                    flex: 1, p: 2.5, borderRadius: 3,
                    border: '1.5px dashed #e0e0e0', textAlign: 'center'
                  }}>
                    <LockIcon sx={{ color: '#bdbdbd', mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600} color="text.disabled">S{sn}</Typography>
                    <Typography variant="caption" color="text.disabled">{t(SESSION_TYPE_KEY(sn))}</Typography>
                  </Paper>
                ))}
              </Box>
            ) : (
              semesters.map(sem => {
                const sn = sem.semester_number;
                const color = SEM_COLORS[sn] || '#95a5a6';
                const pct = sem.total_count > 0 ? Math.round((sem.validated_count / sem.total_count) * 100) : 0;
                const notStarted = !sem.has_started;

                return (
                  <Paper key={sn} elevation={0} sx={{
                    borderRadius: 3,
                    border: `1.5px solid ${notStarted ? '#e0e0e0' : color + '33'}`,
                    overflow: 'hidden', mb: 2,
                    opacity: notStarted ? 0.75 : 1,
                  }}>

                    {/* Semester header */}
                    <Box sx={{
                      px: 2.5, py: 1.8,
                      bgcolor: notStarted ? '#f8f8f8' : color + '12',
                      borderBottom: `1px solid ${notStarted ? '#ebebeb' : color + '33'}`,
                      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                    }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        bgcolor: notStarted ? '#eeeeee' : color + '22',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Typography variant="caption" fontWeight={800} sx={{ color: notStarted ? '#9e9e9e' : color }}>S{sn}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: notStarted ? 'text.secondary' : color }}>
                          {t('dp_semester')} {sn}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t(SESSION_TYPE_KEY(sn))}</Typography>
                      </Box>

                      {/* Progress inline */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {notStarted ? (
                          <Chip
                            icon={<LockIcon sx={{ fontSize: '13px !important' }} />}
                            label={t('dp_notStarted')}
                            size="small"
                            sx={{ height: 22, borderRadius: 1.5, fontWeight: 600, bgcolor: '#f0f0f0', color: '#9e9e9e', fontSize: '0.68rem' }}
                          />
                        ) : (
                          <>
                            <Box sx={{ width: 100 }}>
                              <LinearProgress variant="determinate" value={pct} sx={{
                                height: 6, borderRadius: 3, bgcolor: color + '22',
                                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: pct === 100 ? '#27ae60' : color }
                              }} />
                            </Box>
                            <Chip
                              label={`${sem.validated_count}/${sem.total_count}`}
                              size="small"
                              sx={{ bgcolor: color + '22', color, fontWeight: 700, height: 22, borderRadius: 1.5 }}
                            />
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Module rows */}
                    <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {sem.modules
                        .sort((a, b) => (a.cod_elp || '').localeCompare(b.cod_elp || ''))
                        .map((mod, i) => <ModuleRow key={`${mod.cod_elp}-${i}`} mod={mod} accent={color} />)
                      }
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        );
      })}

      {/* Legend */}
      {by_semester.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2, mt: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1}>
            {t('dp_legendTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { label: 'V',   bg: '#dcfce7', color: '#16a34a', key: 'gt_V'   },
              { label: 'VAR', bg: '#fef3c7', color: '#b45309', key: 'gt_VAR' },
              { label: 'ACR', bg: '#dbeafe', color: '#2563eb', key: 'gt_ACR' },
              { label: 'AC',  bg: '#dcfce7', color: '#16a34a', key: 'gt_AC'  },
              { label: 'VE',  bg: '#dbeafe', color: '#2563eb', key: 'gt_AC'  },
              { label: 'NV',  bg: '#fee2e2', color: '#dc2626', key: 'gt_NV'  },
            ].map(({ label, bg, color, key }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip label={label} size="small" sx={{ height: 20, borderRadius: 1, fontWeight: 700, bgcolor: bg, color, fontSize: '0.7rem' }} />
                <Typography variant="caption" color="text.secondary">{t(key)}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default StudentDiplomaProgress;
